import React, { useEffect, useState } from "react";
import insightData from "./InsightData.json";
import { Breadcrumb } from "components";
import "./InsightPage.scss";
import { translate } from "react-switch-lang";
import PieChart from "../amcharts/PieChart";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import SingleChartCard from "../Cards/SingleChartCard";
import api from "../api/api";
import { Spinner } from "reactstrap";
import { format } from "date-fns";
import toast from "react-hot-toast";

// Helper function to format keys for display
const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Helper function to handle null/undefined/empty values
const handleNullValue = (value) => {
  if (value === null || value === undefined || value === "" || value === 0) {
    return "********";
  }
  return value;
};

// Helper function to safely format display values
const formatDisplayValue = (value, isPercentage = false) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "number" && isNaN(value))
  ) {
    return "********";
  }

  if (isPercentage && typeof value === "number") {
    return `${value}%`;
  }

  return value;
};

const InsightPage = ({ t }) => {
  const [data, setData] = useState(null);
  const { id, type } = useParams();
  const navigate = useHistory().push;
  const [dashboardTitle, setDashboardTitle] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [userList, setUserList] = useState([]);
  const [lowScorerModalTitle, setLowScorerModalTitle] = useState("");
  const [modalType, setModalType] = useState("invited");
  const [staffInvitedList, setStaffInvitedList] = useState([]);
  const [respondedUserList, setRespondedUserList] = useState([]);
  const [quizParticipants, setQuizParticipants] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showLowScorerModal, setShowLowScorerModal] = useState(false);
  const [lowScorers, setLowScorers] = useState([]);
  const [loadingLowScorers, setLoadingLowScorers] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (type === "quiz") {
        try {
          const [completionRes, insightsRes, segmentationRes] =
            await Promise.all([
              api.fetchQuizCompletionStats(id),
              api.fetchQuizInsights(id),
              api.segmentation(id),
            ]);

          const completionData = completionRes.data.data;
          const performanceData = insightsRes.data.data;
          const segmentationData = segmentationRes.data.data;

          setDashboardTitle(performanceData.title || "Quiz Dashboard");

          const quizDashboard = {
            completionRate: {
              title: "Overall Completion Rate",
              completionPercentage: completionData.completionRate,
              notCompleted: completionData.totalNotCompleted,
              assignedQuizzes: completionData.totalAssigned,
            },
            performanceInsights: {
              title: "Performance Insights",
              averageScore: performanceData.averageScore,
              passRate: performanceData.passRate,
              failRate: performanceData.failRate,
              topScorer: performanceData.topScorer,
              lowestScorer: performanceData.lowestScorer,
              mostIncorrectQuestions: performanceData.mostIncorrectQuestions,
            },
            segmentation: {
              title: segmentationData.title,
              byRegion: segmentationData.byRegion,
              byRole: segmentationData.byRole,
            },
          };

          setData({ quizDashboard });
        } catch (error) {
          console.error("Error loading quiz insights:", error);
          setData(insightData);
        }
      } else {
        try {
          const [insightsRes, activityRes, satisfactionRes] = await Promise.all(
            [
              api.fetchSurveyResponseStats(id),
              api.fetchSurveyActivityStats(id),
              api.fetchSurveySatisfactionInsights(id),
            ],
          );

          const insight = insightsRes.data.data;
          const activity = activityRes.data.data;
          const satisfaction = satisfactionRes.data.data;

          // console.log("Satisfaction Data:", satisfaction);
          // console.log("Activity Data:", activity);
          // console.log("Insight Data:", insight);

          setDashboardTitle(insight.title || "Survey Dashboard");

          const surveyDashboard = {
            responseRate: {
              title: "Survey Response Rate",
              overallResponsePercentage: insight.overall.overallResponseRate,
              responsesReceived: insight.overall.totalResponded,
              staffInvited: insight.overall.totalInvited,
              byRegion: insight.byRegion.reduce((regionAcc, region) => {
                regionAcc[region.region] = {
                  outlets: region.outlets.reduce((outletAcc, outlet) => {
                    outletAcc[outlet.outlet] = {
                      roles: outlet.position.reduce((roleAcc, pos) => {
                        roleAcc[pos.position] = pos.responseRate;
                        return roleAcc;
                      }, {}),
                    };
                    return outletAcc;
                  }, {}),
                };
                return regionAcc;
              }, {}),
            },
          };

          const participation = {
            title: "Participation Insights",
            mostActiveRegions: activity.mostActiveRegions?.region || "",
            leastActiveRegions: activity.leastActiveRegions?.region || "",
            mostActiveOutlets: activity.mostActiveOutlets?.outlet || "",
            leastActiveOutlets: activity.leastActiveOutlets?.outlet || "",
          };

          const satisfactionInsights = {
            title: satisfaction.title,
            averageSatisfactionBySurveyType:
              satisfaction.averageSatisfactionBySurveyType,
            scoreDistributionPerQuestion:
              satisfaction.scoreDistributionPerQuestion,
          };

          setData({ surveyDashboard, participation, satisfactionInsights });
        } catch (error) {
          console.error("Error loading survey insights:", error);
          setData(insightData);
        }
      }
    };

    loadData();
  }, [type]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const staffInvitedResponse = await api.totalStaffInvited(id);
        const respondedUserResponse = await api.totalRespondedUser(id);

        const staffInvitedResponseData = staffInvitedResponse.data.data;
        const respondedUserResponseData = respondedUserResponse.data.data;
        // console.log("Total Staff Invited:", staffInvitedResponseData);
        // console.log("Total Responded Users:", respondedUserResponseData);
        setUserList(staffInvitedResponseData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id]);

  const handleCheckboxChange = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === lowScorers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(lowScorers.map((u) => u.id));
    }
  };

  const handleNotifySelected = async () => {
    const payload = {
      quizSurveyId: id,
      message:
        "You have been identified for low activity. Please review and participate.",
      targetUser: selectedUsers,
    };

    // console.log("Sending payload:", payload);

    try {
      const response = await api.notifyUsers(payload);
      // console.log("Notification sent:", response);
      toast.success("Notifications sent successfully!");
      setShowLowScorerModal(false);
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error sending notifications:", error);
      toast.error("Failed to send notifications");
    }
  };

  const handleShowUsers = (typeParam, region = null, resultType = null) => {
    setModalType(typeParam);

    let users = [];

    if (type === "quiz") {
      // For quiz: use staffInvitedList for "invited", quizParticipants for "responded"
      users =
        typeParam === "invited" ? [...staffInvitedList] : [...quizParticipants];
    } else {
      // For survey: existing logic
      users =
        typeParam === "invited"
          ? [...staffInvitedList]
          : [...respondedUserList];
    }

    if (region) {
      users = users.filter((u) => u.region === region);
    }

    if (resultType) {
      users = users.filter((u) => u.result === resultType);
    }

    setUserList(users);
    setShowUserModal(true);
  };

  // NEW: Handle clicking least active region
  const handleLeastActiveRegionClick = async () => {
    setLoadingLowScorers(true);
    setLowScorerModalTitle("Least Active Region Users");
    setShowLowScorerModal(true);

    try {
      const response = await api.fetchLeastActiveUsersByRegion(
        id,
        participation.leastActiveRegions,
      );
      setLowScorers(response.data.data || []);
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error fetching least active users by region:", error);
      toast.error("Failed to load least active users");
      setLowScorers([]);
    } finally {
      setLoadingLowScorers(false);
    }
  };

  // NEW: Handle clicking least active outlet
  const handleLeastActiveOutletClick = async () => {
    setLoadingLowScorers(true);
    setLowScorerModalTitle("Least Active Outlet Users");
    setShowLowScorerModal(true);

    try {
      const response = await api.fetchLeastActiveUsersByOutlet(
        id,
        participation.leastActiveOutlets,
      );
      setLowScorers(response.data.data || []);
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error fetching least active users by outlet:", error);
      toast.error("Failed to load least active users");
      setLowScorers([]);
    } finally {
      setLoadingLowScorers(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (type === "quiz") {
          // Fetch BOTH invited users AND participants for quiz
          const [invitedRes, participantsRes] = await Promise.all([
            api.totalStaffInvited(id), // All users assigned the quiz
            api.totalRespondedUser(id), // Users who completed the quiz
          ]);
          setStaffInvitedList(invitedRes.data.data || []); // All assigned users
          setQuizParticipants(participantsRes.data.data || []); // Only responded users
        } else {
          // Fetch survey users (invited and responded)
          const [invitedRes, respondedRes] = await Promise.all([
            api.totalStaffInvited(id),
            api.totalRespondedUser(id),
          ]);
          setStaffInvitedList(invitedRes.data.data || []);
          setRespondedUserList(respondedRes.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching user data", err);
      }
    };

    fetchUsers();
  }, [id, type]);

  if (!data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner
          animation="border"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        />
      </div>
    );
  }

  const {
    quizDashboard: { completionRate, performanceInsights, segmentation } = {},
    surveyDashboard: { responseRate } = {},
    participation,
    satisfactionInsights,
  } = data;

  const regionChartData = segmentation
    ? Object.entries(segmentation.byRegion).map(([name, value]) => ({
        name: handleNullValue(name),
        value: handleNullValue(value),
      }))
    : [];

  const roleChartData = segmentation
    ? Object.entries(segmentation.byRole).map(([name, value]) => ({
        name: handleNullValue(name),
        value: handleNullValue(value),
      }))
    : [];

  const renderDynamicTable = (obj, disableClick = false) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;

    if (obj.byRegion) {
      return (
        <table className="insight-table">
          <thead>
            <tr>
              <th>Region</th>
              <th>Outlet</th>
              <th>Response Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(obj.byRegion).flatMap(([regionName, regionData]) =>
              Object.entries(regionData.outlets).flatMap(
                ([outletName, outletData]) =>
                  Object.entries(outletData.roles).map(
                    ([roleName, rate], idx) => (
                      <tr
                        key={`${regionName}-${outletName}-${roleName}-${idx}`}
                        onClick={() =>
                          !disableClick &&
                          rate !== 0 &&
                          handleShowUsers("responded", regionName)
                        }
                        style={{
                          cursor:
                            !disableClick && rate !== 0 ? "pointer" : "default",
                        }}
                      >
                        <td>{handleNullValue(regionName)}</td>
                        <td>{handleNullValue(outletName)}</td>
                        <td>{formatDisplayValue(rate, true)}</td>
                      </tr>
                    ),
                  ),
              ),
            )}
          </tbody>
        </table>
      );
    }

    return (
      <table className="insight-table">
        <tbody>
          {Object.entries(obj).map(([key, value]) => {
            const isPercentage =
              typeof value === "number" &&
              key.toLowerCase().includes("percentage");
            const isNumeric = typeof value === "number";
            const isZero = value === 0;

            const isResultKey =
              key.toLowerCase() === "passrate" ||
              key.toLowerCase() === "failrate";
            const clickable =
              !disableClick && (isResultKey || (!isZero && !isResultKey));

            return (
              <tr key={key}>
                <td>{formatKey(key)}</td>
                <td>
                  {isPercentage || isNumeric ? (
                    clickable ? (
                      <span
                        className="clickable"
                        onClick={() =>
                          handleShowUsers(
                            "responded",
                            null,
                            isResultKey
                              ? key.toLowerCase() === "passrate"
                                ? "PASS"
                                : "FAIL"
                              : null,
                          )
                        }
                      >
                        {formatDisplayValue(value, isPercentage)}
                      </span>
                    ) : (
                      <span>{formatDisplayValue(value, isPercentage)}</span>
                    )
                  ) : React.isValidElement(value) ? (
                    value
                  ) : (
                    handleNullValue(String(value))
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="insight-page">
      {type === "quiz" ? (
        <>
          <Breadcrumb title={t(dashboardTitle)} />
          <div className="section">
            <h3>{completionRate.title}</h3>
            {renderDynamicTable({
              "Completion Percentage": (
                <span>
                  {formatDisplayValue(
                    completionRate.completionPercentage,
                    true,
                  )}
                </span>
              ),
              "Not Completed": (
                <span>{handleNullValue(completionRate.notCompleted)}</span>
              ),
              "Assigned Quizzes": (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("invited")}
                >
                  {handleNullValue(completionRate.assignedQuizzes)}
                </span>
              ),
            })}
          </div>

          <div className="section">
            <h3>{performanceInsights.title}</h3>
            {renderDynamicTable({
              "Average Score": (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("responded")}
                >
                  {formatDisplayValue(performanceInsights.averageScore)}
                </span>
              ),
              "Pass Rate": (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("responded", null, "PASS")}
                >
                  {formatDisplayValue(performanceInsights.passRate, true)}
                </span>
              ),
              "Fail Rate": (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("responded", null, "FAIL")}
                >
                  {formatDisplayValue(performanceInsights.failRate, true)}
                </span>
              ),
              "Top Scorer": performanceInsights.topScorer
                ? handleNullValue(performanceInsights.topScorer.name)
                : "",
              "Lowest Scorer": performanceInsights.lowestScorer
                ? handleNullValue(performanceInsights.lowestScorer.name)
                : "",
            })}
          </div>

          <div className="section">
            <h3>Most Incorrect Questions</h3>
            <table className="insight-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Wrong Attempts</th>
                </tr>
              </thead>
              <tbody>
                {performanceInsights.mostIncorrectQuestions?.map((q, i) => (
                  <tr key={i}>
                    <td>{handleNullValue(q.question)}</td>
                    <td>{handleNullValue(q.wrongAttempts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="chart-section">
            <SingleChartCard
              title="Segmentation by Region"
              desc="Distribution of responses by region"
              icon="pie_chart"
              chart={
                <PieChart
                  title="Segmentation by Region"
                  chartID="segmentationPie"
                  data={regionChartData}
                />
              }
            />

            <SingleChartCard
              title="Segmentation by Role"
              desc="Distribution of responses by role"
              icon="bar_chart"
              chart={
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={roleChartData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0062a4df" />
                  </BarChart>
                </ResponsiveContainer>
              }
            />
          </div>
        </>
      ) : (
        <>
          <Breadcrumb title={t(dashboardTitle)} />

          <div className="section">
            <h3>
              <span
                className="material-icons"
                style={{ verticalAlign: "middle", marginRight: "8px" }}
              >
                bar_chart
              </span>
              {responseRate.title}
            </h3>

            {renderDynamicTable({
              overallResponsePercentage: (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("invited")}
                >
                  {formatDisplayValue(
                    responseRate.overallResponsePercentage,
                    true,
                  )}
                </span>
              ),
              responsesReceived: (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("responded")}
                >
                  {handleNullValue(responseRate.responsesReceived)}
                </span>
              ),
              staffInvited: (
                <span
                  className="clickable"
                  onClick={() => handleShowUsers("invited")}
                >
                  {handleNullValue(responseRate.staffInvited)}
                </span>
              ),
            })}
          </div>

          <div className="section-center-table">
            <h3>
              <span
                className="material-icons"
                style={{ verticalAlign: "middle", marginRight: "8px" }}
              >
                bar_chart
              </span>
              Response Rate Overview
            </h3>

            <table className="insight-table">
              {renderDynamicTable(responseRate)}
            </table>
          </div>

          <div className="section">
            <h3>
              <span
                className="material-icons"
                style={{ verticalAlign: "middle", marginRight: "6px" }}
              >
                group
              </span>
              {participation.title}
            </h3>
            {renderDynamicTable({
              mostActiveRegions: handleNullValue(
                participation.mostActiveRegions,
              ),
              leastActiveRegions: (
                <span
                  className="clickable"
                  onClick={handleLeastActiveRegionClick}
                >
                  {handleNullValue(participation.leastActiveRegions)}
                </span>
              ),
              mostActiveOutlets: handleNullValue(
                participation.mostActiveOutlets,
              ),
              leastActiveOutlets: (
                <span
                  className="clickable"
                  onClick={handleLeastActiveOutletClick}
                >
                  {handleNullValue(participation.leastActiveOutlets)}
                </span>
              ),
            })}
          </div>

          <div className="section">
            <h3>
              <span
                className="material-icons"
                style={{ verticalAlign: "middle", marginRight: "6px" }}
              >
                insights
              </span>
              {satisfactionInsights.title}
            </h3>
            <h4>Average Satisfaction by Survey Type</h4>
            {renderDynamicTable(
              satisfactionInsights.averageSatisfactionBySurveyType,
              true,
            )}
          </div>

          <div className="section-center-table">
            <h3>Score Distribution Per Rating Question</h3>
            {satisfactionInsights.scoreDistributionPerQuestion.map((q, i) => (
              <div key={i}>
                <p>
                  <strong>{handleNullValue(q.question)}</strong>
                </p>
                <table className="insight-table">
                  <thead>
                    <tr>
                      {Object.keys(q.distribution).map((score) => (
                        <th key={score}>{handleNullValue(score)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {Object.values(q.distribution).map((count, j) => (
                        <td key={j}>{handleNullValue(count)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}

      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h3 style={{ margin: 0 }}>{dashboardTitle} Respondents</h3>

              <span
                className="material-icons"
                onClick={() => setShowUserModal(false)}
                style={{
                  cursor: "pointer",
                  fontSize: "28px",
                  color: "#333",
                }}
              >
                close
              </span>
            </div>
            <table className="insight-table">
              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Staff Name</th>
                  <th>Region</th>
                  <th>Outlet</th>
                </tr>
              </thead>
              <tbody>
                {userList.map((u) => (
                  <tr
                    key={u.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      localStorage.setItem("Title", dashboardTitle);
                      navigate(`/result/${id}/${type}`);
                    }}
                  >
                    <td>{handleNullValue(u.staffId)}</td>
                    <td>{handleNullValue(u.username)}</td>
                    <td>{handleNullValue(u.region)}</td>
                    <td>{handleNullValue(u.outlet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={() => setShowUserModal(false)}
              style={{ marginTop: "10px" }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showLowScorerModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowLowScorerModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{lowScorerModalTitle}</h3>

            {loadingLowScorers ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <>
                <table className="insight-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedUsers.length === lowScorers.length}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Staff ID</th>
                      <th>Staff Name</th>
                      <th>Region</th>
                      <th>Outlet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowScorers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(u.id)}
                            onChange={() => handleCheckboxChange(u.id)}
                          />
                        </td>
                        <td>{handleNullValue(u.staffId)}</td>
                        <td>{handleNullValue(u.username)}</td>
                        <td>{handleNullValue(u.region)}</td>
                        <td>{handleNullValue(u.outlet)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div
                  style={{ display: "flex", gap: "10px", marginTop: "15px" }}
                >
                  <button onClick={() => setShowLowScorerModal(false)}>
                    Close
                  </button>
                  <button
                    onClick={handleNotifySelected}
                    disabled={selectedUsers.length === 0}
                  >
                    Notify Selected
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default translate(InsightPage);
